import { saveAs } from "file-saver";

import { ONE_INCH_TO_MM } from 'pxc-editor-core/constants';
import {
  DESKTOP,
  SMARTPHONE,
  TABLET,
} from "@/constants";
import { deviceWidth } from '@/globalStyles';

export const unicodeToInt = (unicode) => {
  return parseInt(unicode, 16)
};

export const unicodeToChar = (unicode) => {
  return String.fromCharCode(unicodeToInt(unicode));
};

export const charToUnicode = (char) => {
  return char.codePointAt(0).toString(16);
};

export const intToUnicode = (int) => {
  return int.toString(16);
};

export const mmToInch = (mm, round) => {
  const res = mm / ONE_INCH_TO_MM;
  return round ? (Math.round(res * 1000) / 1000) : res;
};

export const inchToMm = (inch, round) => {
  const res = inch * ONE_INCH_TO_MM;
  return round ? (Math.round(res * 100) / 100) : res;
};

// NOT USED
export const convertMmToPx = (valueInMm, dpi) =>
  (valueInMm * dpi) / ONE_INCH_TO_MM;

// NOT USED
export const convertPxToMm = (valueInPx, dpi) => {
  return valueInPx * (ONE_INCH_TO_MM / dpi);
}

export const getDictPropIndex = (findPropName, dict) => {
  return Object.keys(dict).indexOf(findPropName);
};

export const copyDictByProps = (dict, props) => {
  let res = {};
  props.forEach((prop) => { res[prop] = dict[prop]; });
  return res;
};

export const saveToDevice = (dataBlob, fileName = 'output.bmp') => {
  saveAs(dataBlob, fileName);
};

export const jsonToBlob = (jsonData) => {
  return new Blob([jsonData], {type: "text/json;charset=utf-8"});
};

export const urlDataToBlob = (urlData) => {
  return fetch(urlData)
    .then(res => {
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      URL.revokeObjectURL(res.url);
      return res.blob();
    })
    .catch(error => {
      console.error('Error fetching image:', error);
      throw error;
    });
};

export const handleEventFileUpload = (event, clb) => {
  const {
    target: {
      files: [chosenFile],
    },
  } = event;
  let fileReader = new FileReader();
  fileReader.readAsText(chosenFile);
  fileReader.onloadend = () => {
    clb(fileReader.result, chosenFile.name);
  };
  fileReader.onerror = () => {
    console.error(`Error occurred reading file: ${chosenFile.name}`);
  };
};

export const getOnlyDiffProps = (origData, checkData, acceptNotDefined = false) => {
  const res = {};
  for(const p in checkData) {
    if (
      (origData.hasOwnProperty(p) && origData[p] !== checkData[p]) ||
      (!origData.hasOwnProperty(p) && acceptNotDefined)
    ) {
      res[p] = checkData[p];
    }
  }

  return res;
};

export function isTouchDevice() {
  return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
};

export const detectTargetDevice = () => {
  const screenWidth = Math.min(window.innerWidth, window.outerWidth);

  if (screenWidth <= deviceWidth.smartphone || window.innerWidth < window.innerHeight) {
    return SMARTPHONE; 
  }
  if (mobileAndTabletCheck() || screenWidth <= deviceWidth.tablet || isIosMobileCheck() || isTouchDevice()) {
    return TABLET;
  }
  return DESKTOP;
};

const windowMobileAndTabletRegexp1 = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;
const windowMobileAndTabletRegexp2 = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
let windowMobileAndTabletResult = null;
export const mobileAndTabletCheck = () => {
  if (windowMobileAndTabletResult === null) {
    windowMobileAndTabletResult = false;
    (function(a) {
      if (windowMobileAndTabletRegexp1.test(a) || windowMobileAndTabletRegexp2.test(a.substr(0, 4)))
        windowMobileAndTabletResult = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
  }
  return windowMobileAndTabletResult;
};

let windowIsIosMobileResult = null;
export const isIosMobileCheck = () => {
  if(windowIsIosMobileResult === null) {
    windowIsIosMobileResult = /iPad|iPhone|iPod/.test(navigator.platform)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }
  return windowIsIosMobileResult;
};